import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, Plus, Trash2, X, AlertCircle } from 'lucide-react'
import { api } from '../services/api'
import { formatCurrency, getCurrentMonth, getMonthLabel, getPreviousMonth, getNextMonth, formatDate } from '../utils/format'

export default function Expenses() {
  const [categories, setCategories] = useState([])
  const [expenses, setExpenses] = useState([])
  const [month, setMonth] = useState(getCurrentMonth())
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({ category_id: '', amount: '', description: '', expense_date: new Date().toISOString().split('T')[0], is_unnecessary: false })

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [cats, exps] = await Promise.all([
        api.getCategories(),
        api.getExpenses(month),
      ])
      setCategories(cats.categories || [])
      setExpenses(exps.expenses || [])
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }, [month])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    const onEvents = (e) => {
      const evs = e.detail || []
      if (evs.some(ev => ev.entity === 'expense' || ev.entity === 'budget' || ev.entity === 'payment')) {
        fetchData()
      }
    }
    window.addEventListener('fb:events', onEvents)
    return () => window.removeEventListener('fb:events', onEvents)
  }, [fetchData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.category_id || !formData.amount) return
    try {
      await api.addExpense({
        ...formData,
        amount: parseFloat(formData.amount),
      })
      setShowForm(false)
      setFormData({ category_id: '', amount: '', description: '', expense_date: new Date().toISOString().split('T')[0], is_unnecessary: false })
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette dépense ?')) return
    try {
      await api.deleteExpense(id)
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0)

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-primary-700 border-t-transparent rounded-full animate-spin" /></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => setMonth(getPreviousMonth(month))} className="p-2 rounded-lg active:bg-slate-100">
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-lg font-semibold capitalize">{getMonthLabel(month)}</h2>
        <button onClick={() => setMonth(getNextMonth(month))} disabled={month >= getCurrentMonth()} className="p-2 rounded-lg active:bg-slate-100 disabled:opacity-30">
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="card bg-red-50 border-red-200 text-center">
        <p className="text-sm text-slate-500">Total dépenses</p>
        <p className="text-2xl font-bold text-red-500">{formatCurrency(totalExpenses)}</p>
      </div>

      {/* Add button */}
      <button onClick={() => setShowForm(true)} className="btn-primary w-full flex items-center justify-center gap-2">
        <Plus size={18} /> Ajouter une dépense
      </button>

      {/* Expense list */}
      <div className="space-y-2">
        {expenses.length === 0 && (
          <div className="card text-center py-8 text-slate-400">
            <p>Aucune dépense ce mois-ci</p>
          </div>
        )}
        {expenses.map((exp) => (
          <div key={exp.id} className="card flex items-center gap-3">
            <span className="text-2xl">{exp.category_icon}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">
                {exp.description || exp.category_name}
                {exp.is_unnecessary && <AlertCircle size={14} className="inline ml-1 text-amber-500" />}
              </p>
              <p className="text-xs text-slate-400">
                {exp.parent_name ? `${exp.parent_name} • ` : ''}{formatDate(exp.expense_date)} • {exp.user_name}
              </p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-red-500">{formatCurrency(exp.amount)}</p>
            </div>
            <button onClick={() => handleDelete(exp.id)} className="p-1.5 text-slate-300 active:text-red-500">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-sm max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Nouvelle dépense</h3>
              <button onClick={() => setShowForm(false)} className="p-1"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="label">Catégorie</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="input"
                  required
                >
                  <option value="">Sélectionner...</option>
                  {categories.map((parent) => (
                    parent.children && parent.children.length > 0 ? (
                      <optgroup key={parent.id} label={`${parent.icon} ${parent.name}`}>
                        {parent.children.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                        ))}
                      </optgroup>
                    ) : (
                      <option key={parent.id} value={parent.id}>{parent.icon} {parent.name}</option>
                    )
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Montant (FCFA)</label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="input"
                  placeholder="0"
                  required
                />
              </div>
              <div>
                <label className="label">Description (optionnel)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input"
                  placeholder="Ex: Courses au marché..."
                />
              </div>
              <div>
                <label className="label">Date</label>
                <input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  className="input"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_unnecessary}
                  onChange={(e) => setFormData({ ...formData, is_unnecessary: e.target.checked })}
                  className="w-5 h-5 rounded accent-primary-700"
                />
                <span className="text-sm">Marquer comme dépense inutile</span>
              </label>
              <button type="submit" className="btn-primary w-full">Enregistrer</button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
